/**
 * localStorage persistence for Bandmate sessions (SPEC §2 "Persistence",
 * §10 "storage"). Everything is wrapped in try/catch: a private-mode browser,
 * a full quota, or a corrupted value must never take the app down.
 *
 * `load()` accepts only `version === 1` sessions that pass a minimal shape
 * check; anything else yields `null` so the caller falls back to the demo
 * preset. `createAutosave()` subscribes to a store and writes the committed
 * session after a debounce, ignoring transient (audition) changes.
 *
 * Every function takes an optional `storage` so tests can inject a fake that
 * throws; it defaults to `globalThis.localStorage` when that is reachable.
 */
import {
  BARS,
  BASS_PATTERNS,
  CHORD_RHYTHMS,
  DRUM_LANES,
  MODES,
  STYLES,
  TONICS,
  TRACK_IDS,
  type Session,
} from '../types';
import type { ChangeMeta, Store } from './store-api';

/** The single localStorage key; the suffix is the session schema version. */
export const KEY = 'bandmate:v1';

/** The subset of the DOM `Storage` interface this module needs. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** `globalThis.localStorage` when available, else null. Access itself can throw (sandboxed iframes). */
export function defaultStorage(): StorageLike | null {
  try {
    const g = globalThis as { localStorage?: StorageLike };
    const ls = g.localStorage;
    return ls && typeof ls.getItem === 'function' ? ls : null;
  } catch {
    return null;
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

const AUTHORS: readonly string[] = ['human', 'agent'];

function isOneOf(list: readonly string[], v: unknown): boolean {
  return typeof v === 'string' && list.includes(v);
}

/** Mute/solo/volume flags every track carries; a missing volume would feed NaN dB to the engine. */
function hasTrackFlags(v: Record<string, unknown>): boolean {
  return typeof v['muted'] === 'boolean' && typeof v['solo'] === 'boolean' && isFiniteNumber(v['volume']);
}

function isNote(v: unknown): boolean {
  return (
    isRecord(v) &&
    isFiniteNumber(v['step']) &&
    isFiniteNumber(v['pitch']) &&
    isFiniteNumber(v['length']) &&
    isFiniteNumber(v['velocity']) &&
    isOneOf(AUTHORS, v['by'])
  );
}

function isHit(v: unknown): boolean {
  return (
    isRecord(v) &&
    isFiniteNumber(v['step']) &&
    isOneOf(DRUM_LANES, v['lane']) &&
    isFiniteNumber(v['velocity']) &&
    isOneOf(AUTHORS, v['by'])
  );
}

function isMelodicTrack(v: unknown): boolean {
  return isRecord(v) && hasTrackFlags(v) && Array.isArray(v['notes']) && v['notes'].every(isNote);
}

function isDrumTrack(v: unknown): boolean {
  return isRecord(v) && hasTrackFlags(v) && Array.isArray(v['hits']) && v['hits'].every(isHit);
}

/**
 * Structural check for a persisted `Session`: version 1, a known key, numeric
 * tempo/swing, a known style, boolean metronome, four tracks with flags whose
 * notes/hits have numeric fields and a known author/lane, valid progression /
 * rhythm / bass-pattern metadata, and a `ui` selection inside 0..3.
 *
 * Nothing normalises a loaded session before the engine reads it, so every
 * field Tone.js or the tools dereference is checked here; ranges (tempo,
 * pitch windows) are left to the store and tools.
 */
export function isSession(v: unknown): v is Session {
  if (!isRecord(v) || v['version'] !== 1) return false;
  const key = v['key'];
  if (!isRecord(key)) return false;
  if (!isOneOf(TONICS, key['tonic']) || !isOneOf(MODES, key['mode'])) return false;
  if (!isFiniteNumber(v['tempo']) || !isFiniteNumber(v['swing'])) return false;
  if (!isOneOf(STYLES, v['style']) || typeof v['metronome'] !== 'boolean') return false;
  const tracks = v['tracks'];
  if (!isRecord(tracks)) return false;
  if (!isDrumTrack(tracks['drums'])) return false;
  if (!isMelodicTrack(tracks['bass']) || !isMelodicTrack(tracks['chords']) || !isMelodicTrack(tracks['lead'])) return false;
  const progression = v['progression'];
  if (progression !== null && !(Array.isArray(progression) && progression.every(isRecord))) return false;
  if (v['progressionRhythm'] !== null && !isOneOf(CHORD_RHYTHMS, v['progressionRhythm'])) return false;
  if (v['bassPattern'] !== null && !isOneOf(BASS_PATTERNS, v['bassPattern'])) return false;
  const ui = v['ui'];
  if (!isRecord(ui) || !isOneOf(TRACK_IDS, ui['selectedTrack'])) return false;
  if (!isFiniteNumber(ui['selectedBar']) || ui['selectedBar'] < 0 || ui['selectedBar'] >= BARS) return false;
  return true;
}

/** Read the saved session; null when absent, unparsable, the wrong version, or malformed. */
export function load(storage: StorageLike | null = defaultStorage()): Session | null {
  try {
    if (!storage) return null;
    const raw = storage.getItem(KEY);
    if (raw === null || raw === undefined) return null;
    const parsed: unknown = JSON.parse(raw);
    return isSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Persist a session. Returns false (never throws) when storage is missing, full, or the value cannot serialise. */
export function save(s: Session, storage: StorageLike | null = defaultStorage()): boolean {
  try {
    if (!storage) return false;
    storage.setItem(KEY, JSON.stringify(s));
    return true;
  } catch {
    return false;
  }
}

/** Remove the saved session. Never throws. */
export function clear(storage: StorageLike | null = defaultStorage()): void {
  try {
    storage?.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Sources that change the committed session and therefore deserve a save. */
const SAVED_SOURCES: ReadonlySet<ChangeMeta['source']> = new Set(['mutation', 'undo', 'load']);

/**
 * Subscribe to `store` and save its committed session `delayMs` after the last
 * change. Transient (audition) and mode changes never trigger a save. Returns
 * a disposer that flushes any pending save and unsubscribes.
 */
export function createAutosave(store: Store, delayMs = 300, storage: StorageLike | null = defaultStorage()): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    timer = null;
    save(store.get(), storage);
  };

  const unsubscribe = store.subscribe((_s, meta) => {
    if (!SAVED_SOURCES.has(meta.source)) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(flush, delayMs);
  });

  return () => {
    unsubscribe();
    if (timer !== null) {
      clearTimeout(timer);
      flush();
    }
  };
}
